import { lazy, Suspense, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ViewToolbar, GridToolbarItems, GanttToolbarItems } from './ViewToolbar';
import { SplitView } from './SplitView';
import { CardView } from './CardView';
import { CalendarView } from './CalendarView';
import { useUrlViewMode, ViewMode } from '@/stores/useViewStore';
import { useGanttStore } from '@/stores/useGanttStore';
import { Separator } from '@/components/ui/separator';
import type { GanttViewMode } from '@/lib/gantt/types';

// Lazy load heavy components
const GanttView = lazy(() => import('@/components/gantt/GanttView'));

interface ViewContainerProps {
  sheetId: string;
  gridView: React.ReactNode;
  columns?: { id: string; name: string; visible: boolean }[];
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;
  onFilter?: () => void;
  onSort?: () => void;
  onGroup?: () => void;
}

// Loading fallback component
function ViewLoading({ message = 'Loading view...' }: { message?: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex items-center gap-2 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span>{message}</span>
      </div>
    </div>
  );
}

export function ViewContainer({
  sheetId,
  gridView,
  columns = [],
  onColumnVisibilityChange,
  onFilter,
  onSort,
  onGroup,
}: ViewContainerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const ganttRef = useRef<{ scrollToToday: () => void } | null>(null);

  // View mode from URL and store
  const { currentMode, setMode } = useUrlViewMode(sheetId);

  // View store for split config and selection (used for future row highlighting)
  // const { hoveredTaskId, setHoveredTaskId } = useViewStore();

  // Gantt store for display options
  const {
    viewMode: ganttViewMode,
    showDependencies,
    showProgress,
    showCriticalPath,
    showBaselines,
    setViewMode: setGanttViewMode,
    toggleDependencies,
    toggleProgress,
    toggleCriticalPath,
    toggleBaselines,
  } = useGanttStore();

  // Handle view mode change with URL update
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setMode(mode);
      // Update URL
      const newParams = new URLSearchParams(searchParams);
      newParams.set('view', mode);
      setSearchParams(newParams, { replace: true });
    },
    [setMode, searchParams, setSearchParams]
  );

  // Gantt toolbar handlers
  const handleGanttViewModeChange = useCallback(
    (mode: GanttViewMode) => {
      setGanttViewMode(mode);
    },
    [setGanttViewMode]
  );

  const handleTodayClick = useCallback(() => {
    ganttRef.current?.scrollToToday();
  }, []);

  const handleZoomIn = useCallback(() => {
    const modes: GanttViewMode[] = ['Year', 'Month', 'Week', 'Day'];
    const currentIndex = modes.indexOf(ganttViewMode || 'Week');
    if (currentIndex < modes.length - 1) {
      setGanttViewMode(modes[currentIndex + 1]);
    }
  }, [ganttViewMode, setGanttViewMode]);

  const handleZoomOut = useCallback(() => {
    const modes: GanttViewMode[] = ['Year', 'Month', 'Week', 'Day'];
    const currentIndex = modes.indexOf(ganttViewMode || 'Week');
    if (currentIndex > 0) {
      setGanttViewMode(modes[currentIndex - 1]);
    }
  }, [ganttViewMode, setGanttViewMode]);

  const handleFitToScreen = useCallback(() => {
    setGanttViewMode('Week');
  }, [setGanttViewMode]);

  // Sync URL on mount
  useEffect(() => {
    const viewFromUrl = searchParams.get('view') as ViewMode | null;
    if (viewFromUrl && ['grid', 'gantt', 'split', 'card', 'calendar'].includes(viewFromUrl)) {
      if (viewFromUrl !== currentMode) {
        setMode(viewFromUrl);
      }
    }
  }, []);

  // Render the appropriate toolbar based on view mode
  const renderToolbar = () => {
    switch (currentMode) {
      case 'grid':
        return (
          <ViewToolbar currentMode={currentMode} onModeChange={handleViewModeChange}>
            <GridToolbarItems
              columns={columns}
              onColumnVisibilityChange={onColumnVisibilityChange || (() => {})}
              onFilter={onFilter}
              onSort={onSort}
              onGroup={onGroup}
            />
          </ViewToolbar>
        );

      case 'gantt':
        return (
          <ViewToolbar currentMode={currentMode} onModeChange={handleViewModeChange}>
            <GanttToolbarItems
              viewMode={ganttViewMode || 'Week'}
              showDependencies={showDependencies}
              showProgress={showProgress}
              showCriticalPath={showCriticalPath}
              showBaselines={showBaselines}
              onViewModeChange={handleGanttViewModeChange}
              onTodayClick={handleTodayClick}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onFitToScreen={handleFitToScreen}
              onToggleDependencies={toggleDependencies}
              onToggleProgress={toggleProgress}
              onToggleCriticalPath={toggleCriticalPath}
              onToggleBaselines={toggleBaselines}
            />
          </ViewToolbar>
        );

      case 'split':
        return (
          <ViewToolbar currentMode={currentMode} onModeChange={handleViewModeChange}>
            {/* Combined toolbar for split view */}
            <GridToolbarItems
              columns={columns}
              onColumnVisibilityChange={onColumnVisibilityChange || (() => {})}
              onFilter={onFilter}
              onSort={onSort}
              onGroup={onGroup}
            />
            <Separator orientation="vertical" className="mx-1 h-6" />
            <GanttToolbarItems
              viewMode={ganttViewMode || 'Week'}
              showDependencies={showDependencies}
              showProgress={showProgress}
              showCriticalPath={showCriticalPath}
              showBaselines={showBaselines}
              onViewModeChange={handleGanttViewModeChange}
              onTodayClick={handleTodayClick}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onFitToScreen={handleFitToScreen}
              onToggleDependencies={toggleDependencies}
              onToggleProgress={toggleProgress}
              onToggleCriticalPath={toggleCriticalPath}
              onToggleBaselines={toggleBaselines}
            />
          </ViewToolbar>
        );

      case 'card':
      case 'calendar':
        return (
          <ViewToolbar currentMode={currentMode} onModeChange={handleViewModeChange}>
            {/* Basic toolbar for card/calendar views */}
          </ViewToolbar>
        );

      default:
        return (
          <ViewToolbar currentMode={currentMode} onModeChange={handleViewModeChange} />
        );
    }
  };

  // Render the appropriate view content
  const renderContent = () => {
    switch (currentMode) {
      case 'grid':
        return gridView;

      case 'gantt':
        return (
          <Suspense fallback={<ViewLoading message="Loading Gantt chart..." />}>
            <GanttView sheetId={sheetId} />
          </Suspense>
        );

      case 'split':
        return (
          <SplitView
            sheetId={sheetId}
            leftPanel={gridView}
            rightPanel={
              <Suspense fallback={<ViewLoading message="Loading Gantt chart..." />}>
                <GanttView sheetId={sheetId} />
              </Suspense>
            }
            leftTitle="Grid"
            rightTitle="Gantt"
          />
        );

      case 'card':
        return <CardView sheetId={sheetId} />;

      case 'calendar':
        return <CalendarView sheetId={sheetId} />;

      default:
        return gridView;
    }
  };

  return (
    <div className="flex h-full flex-col">
      {renderToolbar()}
      <div className="flex-1 overflow-hidden">{renderContent()}</div>
    </div>
  );
}

export default ViewContainer;
